/**
 * PlacesView - Google Places discovery with map view
 * Split layout: results list on left, Google Map on right
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  APIProvider,
  Map,
  Marker,
  InfoWindow,
  useMap,
  useApiIsLoaded,
} from "@vis.gl/react-google-maps";
import { SpinnerIcon, CheckIcon } from "@/components/icons";
import { ComboboxInput } from "@/components/ui/ComboboxInput";
import { useAppStore } from "@/core/store/useAppStore";
import { useSearchPlaces, useImportPlaces } from "./hooks/useLeads";
import { leadService } from "./Lead.service";
import type { PlaceResult } from "./Lead.types";
import type { Client } from "@/types";

const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const DEFAULT_CENTER = { lat: 41.9028, lng: 12.4964 }; // Rome
const DEFAULT_ZOOM = 6;

function MapController({
  results,
  activeIndex,
}: {
  results: PlaceResult[];
  activeIndex: number | null;
}) {
  const map = useMap();

  // Fit bounds after search results change
  useEffect(() => {
    if (!map || results.length === 0) return;

    const validResults = results.filter(
      (r) => r.latitude != null && r.longitude != null
    );
    if (validResults.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    validResults.forEach((r) => {
      bounds.extend({ lat: r.latitude!, lng: r.longitude! });
    });
    map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
  }, [map, results]);

  // Pan to active marker
  useEffect(() => {
    if (!map || activeIndex == null) return;
    const place = results[activeIndex];
    if (!place || place.latitude == null || place.longitude == null) return;
    map.panTo({ lat: place.latitude, lng: place.longitude });
  }, [map, activeIndex, results]);

  return null;
}

function ClientMarkers({
  clients,
  activeIndex,
  onMarkerClick,
  onClose,
}: {
  clients: Client[];
  activeIndex: number | null;
  onMarkerClick: (idx: number) => void;
  onClose: () => void;
}) {
  const apiIsLoaded = useApiIsLoaded();
  if (!apiIsLoaded) return null;

  return (
    <>
      {clients.map((client, idx) => (
        <React.Fragment key={`client-${client.id}`}>
          <Marker
            position={{ lat: client.latitude!, lng: client.longitude! }}
            onClick={() => onMarkerClick(idx)}
            icon={{
              url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
              scaledSize: new google.maps.Size(24, 24),
            }}
          />
          {activeIndex === idx && (
            <InfoWindow
              position={{ lat: client.latitude!, lng: client.longitude! }}
              onCloseClick={onClose}
            >
              <div className="max-w-[200px]">
                <h3 className="font-semibold text-sm text-blue-700">
                  {client.clientName}
                </h3>
                {client.sector && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {client.sector}
                  </p>
                )}
                <p className="text-xs text-blue-600 mt-1 font-medium">
                  Active Client
                </p>
              </div>
            </InfoWindow>
          )}
        </React.Fragment>
      ))}
    </>
  );
}

function formatCachedDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

type CachePrompt = {
  cachedAt: string;
  resultCount: number;
};

export function PlacesView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const city = searchParams.get("city") || "";
  const country = searchParams.get("country") || "Italy";

  const setParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const setCategory = useCallback((v: string) => setParam("category", v), [setParam]);
  const setCity = useCallback((v: string) => setParam("city", v), [setParam]);
  const setCountry = useCallback((v: string) => setParam("country", v), [setParam]);

  const [results, setResults] = useState<PlaceResult[]>([]);
  const [existingNames, setExistingNames] = useState<Set<string>>(new Set());
  const [savedIndices, setSavedIndices] = useState<Set<number>>(new Set());
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [saveFeedback, setSaveFeedback] = useState<string | null>(null);
  const [cachedAt, setCachedAt] = useState<string | null>(null);
  const [cachePrompt, setCachePrompt] = useState<CachePrompt | null>(null);
  const [checkingCache, setCheckingCache] = useState(false);

  // POI click state (clicking businesses directly on the map)
  const [poiPlace, setPoiPlace] = useState<(PlaceResult & { isExistingLead: boolean }) | null>(null);
  const [poiLoading, setPoiLoading] = useState(false);
  const [poiSaved, setPoiSaved] = useState(false);

  const [activeClientIndex, setActiveClientIndex] = useState<number | null>(null);

  const listRefs = useRef<(HTMLDivElement | null)[]>([]);
  const searchMutation = useSearchPlaces();
  const importMutation = useImportPlaces();

  // Active clients with coordinates for map markers
  const clients = useAppStore((s) => s.clients);
  const clientsWithCoords = clients.filter(
    (c) =>
      c.status !== "ARCHIVED" &&
      c.latitude != null &&
      c.longitude != null
  );

  // Load category suggestions
  useEffect(() => {
    leadService.getPlaceCategories().then((data) => {
      setCategories(data.categories);
      setCities(data.cities);
    });
  }, []);

  const executeSearch = async (useCache: boolean) => {
    setCachePrompt(null);
    setSavedIndices(new Set());
    setActiveIndex(null);
    setSaveFeedback(null);
    setCachedAt(null);

    const result = await searchMutation.mutateAsync({
      category: category.trim(),
      city: city.trim(),
      country: country.trim(),
      useCache,
    });

    setResults(result.places);
    setExistingNames(new Set(result.existingNames));
    setCachedAt(result.cachedAt);
    listRefs.current = new Array(result.places.length).fill(null);
  };

  const handleSearch = async () => {
    if (!category.trim() || !city.trim()) return;

    setCheckingCache(true);
    try {
      const cacheInfo = await leadService.checkPlacesCache(
        category.trim(),
        city.trim(),
        country.trim()
      );

      if (cacheInfo.hasCache && cacheInfo.cachedAt) {
        setCachePrompt({
          cachedAt: cacheInfo.cachedAt,
          resultCount: cacheInfo.resultCount ?? 0,
        });
      } else {
        await executeSearch(false);
      }
    } catch {
      await executeSearch(false);
    } finally {
      setCheckingCache(false);
    }
  };

  const handleSave = useCallback(
    async (index: number) => {
      if (savedIndices.has(index)) return;
      const place = results[index];

      await importMutation.mutateAsync([place]);
      setSavedIndices((prev) => new Set(prev).add(index));
      setExistingNames((prev) => new Set(prev).add(place.name));
      setSaveFeedback(`Saved "${place.name}" as lead`);
      setTimeout(() => setSaveFeedback(null), 3000);
    },
    [results, savedIndices, importMutation]
  );

  const handleListItemClick = (index: number) => {
    setActiveIndex(index);
  };

  const handleMarkerClick = (index: number) => {
    setActiveIndex(index);
    setActiveClientIndex(null);
    setPoiPlace(null);
    setPoiSaved(false);
    const el = listRefs.current[index];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleMapClick = useCallback(
    async (e: { detail: { placeId?: string | null }; stop: () => void }) => {
      const placeId = e.detail?.placeId;
      if (!placeId) {
        setPoiPlace(null);
        setPoiSaved(false);
        return;
      }

      // Prevent the default info window from showing
      e.stop();

      setActiveIndex(null);
      setActiveClientIndex(null);
      setPoiLoading(true);
      setPoiSaved(false);

      try {
        const { place } = await leadService.getPlaceDetails(placeId);
        setPoiPlace(place);
      } catch (err) {
        console.error("Failed to fetch place details:", err);
        setPoiPlace(null);
      } finally {
        setPoiLoading(false);
      }
    },
    []
  );

  const handleSavePoi = useCallback(async () => {
    if (!poiPlace || poiPlace.isExistingLead || poiSaved) return;

    await importMutation.mutateAsync([poiPlace]);
    setPoiSaved(true);
    setExistingNames((prev) => new Set(prev).add(poiPlace.name));
    setSaveFeedback(`Saved "${poiPlace.name}" as lead`);
    setTimeout(() => setSaveFeedback(null), 3000);
  }, [poiPlace, poiSaved, importMutation]);

  const isExistingLead = (place: PlaceResult) => existingNames.has(place.name);

  return (
    <APIProvider apiKey={MAPS_API_KEY}>
      <div className="h-full flex px-6 pt-6">
        {/* Left Panel */}
        <div className="w-[420px] shrink-0 flex flex-col border-r border-gray-200 bg-white">
          {/* Search Bar */}
          <div className="shrink-0 p-4 border-b border-gray-200 space-y-3">
            <div className="flex gap-2">
              <div className="flex-1">
                <ComboboxInput
                  value={category}
                  onChange={setCategory}
                  options={categories}
                  onKeyDown={handleKeyDown}
                  placeholder="Category (e.g. Dentista)"
                />
              </div>
              <div className="flex-1">
                <ComboboxInput
                  value={city}
                  onChange={setCity}
                  options={cities}
                  onKeyDown={handleKeyDown}
                  placeholder="City (e.g. Bologna)"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={handleSearch}
                disabled={
                  !category.trim() ||
                  !city.trim() ||
                  searchMutation.isPending ||
                  checkingCache
                }
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {(searchMutation.isPending || checkingCache) && (
                  <SpinnerIcon className="w-4 h-4" />
                )}
                Search
              </button>
            </div>

            {/* Cache prompt */}
            {cachePrompt && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 mb-2">
                  Cached results available ({cachePrompt.resultCount} places, {formatCachedDate(cachePrompt.cachedAt)})
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => executeSearch(true)}
                    disabled={searchMutation.isPending}
                    className="flex-1 px-3 py-1.5 text-xs font-medium bg-white border border-amber-300 text-amber-800 rounded-lg hover:bg-amber-100 disabled:opacity-50"
                  >
                    Use Cached
                  </button>
                  <button
                    onClick={() => executeSearch(false)}
                    disabled={searchMutation.isPending}
                    className="flex-1 px-3 py-1.5 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                  >
                    Fresh Search
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Results List */}
          <div className="flex-1 overflow-y-auto">
            {searchMutation.isError && (
              <div className="m-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                {searchMutation.error instanceof Error
                  ? searchMutation.error.message
                  : "Search failed"}
              </div>
            )}

            {results.length === 0 &&
              !searchMutation.isPending &&
              !searchMutation.isError && (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                  Search for places to see results
                </div>
              )}

            {results.map((place, index) => {
              const isExisting = isExistingLead(place);
              const isSaved = savedIndices.has(index);

              return (
                <div
                  key={index}
                  ref={(el) => {
                    listRefs.current[index] = el;
                  }}
                  onClick={() => handleListItemClick(index)}
                  className={`p-3 border-b border-gray-100 cursor-pointer transition-colors ${
                    activeIndex === index
                      ? "bg-blue-50 border-l-2 border-l-blue-500"
                      : isExisting && !isSaved
                        ? "bg-green-50/50 hover:bg-green-50"
                        : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-900 truncate">
                          {place.name}
                        </span>
                        {(isExisting || isSaved) && (
                          <span className="shrink-0 inline-flex items-center gap-0.5 text-xs text-green-600 font-medium">
                            <CheckIcon className="w-3 h-3" />
                            Lead
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {place.address}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        {place.googleRating != null && (
                          <span className="text-amber-600 font-medium">
                            {place.googleRating} ({place.googleReviews ?? 0})
                          </span>
                        )}
                        {place.phone && <span>{place.phone}</span>}
                        <a
                          href={`https://www.google.com/search?q=${encodeURIComponent(place.name + " " + (place.address || city))}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-blue-500 hover:text-blue-700 hover:underline"
                        >
                          Search
                        </a>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSave(index);
                      }}
                      disabled={
                        isSaved || isExisting || importMutation.isPending
                      }
                      className={`shrink-0 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${
                        isSaved || isExisting
                          ? "bg-green-100 text-green-700 cursor-default"
                          : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                      }`}
                    >
                      {isSaved || isExisting ? "Saved" : "Save"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Status Bar */}
          <div className="shrink-0 px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 flex items-center justify-between">
            <span>
              {results.length > 0
                ? `${results.length} results`
                : "No results"}
              {cachedAt && (
                <span className="ml-2 text-amber-600">
                  (cached {formatCachedDate(cachedAt)})
                </span>
              )}
            </span>
            {saveFeedback && (
              <span className="text-green-600 font-medium">{saveFeedback}</span>
            )}
          </div>
        </div>

        {/* Right Panel - Map */}
        <div className="flex-1 relative">
          {poiLoading && (
            <div className="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-lg px-4 py-2 flex items-center gap-2 text-sm text-gray-600">
              <SpinnerIcon className="w-4 h-4" />
              Loading place details...
            </div>
          )}
          <Map
            defaultCenter={DEFAULT_CENTER}
            defaultZoom={DEFAULT_ZOOM}
            gestureHandling="greedy"
            disableDefaultUI={false}
            clickableIcons={true}
            onClick={handleMapClick}
            style={{ width: "100%", height: "100%" }}
          >
            <MapController results={results} activeIndex={activeIndex} />

            {results.map((place, index) => {
              if (place.latitude == null || place.longitude == null)
                return null;

              const isSaved = savedIndices.has(index) || isExistingLead(place);
              const pinUrl = isSaved
                ? "https://maps.google.com/mapfiles/ms/icons/green-dot.png"
                : "https://maps.google.com/mapfiles/ms/icons/red-dot.png";

              return (
                <React.Fragment key={index}>
                  <Marker
                    position={{ lat: place.latitude, lng: place.longitude }}
                    onClick={() => handleMarkerClick(index)}
                    icon={pinUrl}
                  />
                  {activeIndex === index && (
                    <InfoWindow
                      position={{
                        lat: place.latitude,
                        lng: place.longitude,
                      }}
                      onCloseClick={() => setActiveIndex(null)}
                    >
                      <div className="max-w-[250px]">
                        <h3 className="font-semibold text-sm mb-1">
                          {place.name}
                        </h3>
                        <p className="text-xs text-gray-600 mb-1">
                          {place.address}
                        </p>
                        {place.googleRating != null && (
                          <p className="text-xs text-amber-600 mb-1">
                            {place.googleRating} stars ({place.googleReviews}{" "}
                            reviews)
                          </p>
                        )}
                        {place.phone && (
                          <p className="text-xs text-gray-600 mb-1">
                            {place.phone}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mb-2">
                          {place.website && (
                            <a
                              href={place.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Website
                            </a>
                          )}
                          <a
                            href={`https://www.google.com/search?q=${encodeURIComponent(place.name + " " + (place.address || city))}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Google
                          </a>
                        </div>
                        <button
                          onClick={() => handleSave(index)}
                          disabled={
                            isSaved || importMutation.isPending
                          }
                          className={`w-full px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                            isSaved
                              ? "bg-green-100 text-green-700 cursor-default"
                              : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                          }`}
                        >
                          {isSaved ? "Saved as Lead" : "Save as Lead"}
                        </button>
                      </div>
                    </InfoWindow>
                  )}
                </React.Fragment>
              );
            })}

            {/* Active client markers */}
            <ClientMarkers
              clients={clientsWithCoords}
              activeIndex={activeClientIndex}
              onMarkerClick={(idx) => {
                setActiveIndex(null);
                setPoiPlace(null);
                setActiveClientIndex(idx);
              }}
              onClose={() => setActiveClientIndex(null)}
            />

            {/* POI InfoWindow - shown when clicking a business on the map */}
            {poiPlace && poiPlace.latitude != null && poiPlace.longitude != null && (
              <InfoWindow
                position={{ lat: poiPlace.latitude, lng: poiPlace.longitude }}
                onCloseClick={() => {
                  setPoiPlace(null);
                  setPoiSaved(false);
                }}
              >
                <div className="max-w-[250px]">
                  <h3 className="font-semibold text-sm mb-1">
                    {poiPlace.name}
                  </h3>
                  <p className="text-xs text-gray-600 mb-1">
                    {poiPlace.address}
                  </p>
                  {poiPlace.sector && (
                    <p className="text-xs text-gray-500 mb-1">
                      {poiPlace.sector}
                    </p>
                  )}
                  {poiPlace.googleRating != null && (
                    <p className="text-xs text-amber-600 mb-1">
                      {poiPlace.googleRating} stars ({poiPlace.googleReviews} reviews)
                    </p>
                  )}
                  {poiPlace.phone && (
                    <p className="text-xs text-gray-600 mb-1">
                      {poiPlace.phone}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mb-2">
                    {poiPlace.website && (
                      <a
                        href={poiPlace.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Website
                      </a>
                    )}
                    {poiPlace.googleUrl && (
                      <a
                        href={poiPlace.googleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Google Maps
                      </a>
                    )}
                  </div>
                  <button
                    onClick={handleSavePoi}
                    disabled={
                      poiSaved ||
                      poiPlace.isExistingLead ||
                      importMutation.isPending
                    }
                    className={`w-full px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      poiSaved || poiPlace.isExistingLead
                        ? "bg-green-100 text-green-700 cursor-default"
                        : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    }`}
                  >
                    {poiSaved || poiPlace.isExistingLead
                      ? "Saved as Lead"
                      : "Save as Lead"}
                  </button>
                </div>
              </InfoWindow>
            )}
          </Map>
        </div>
      </div>
    </APIProvider>
  );
}
